import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { ClockIcon } from 'lucide-react';
import { WhatsAppIcon, YouTubeIcon } from './components/Icons';
export function App() {
  useEffect(() => {
    document.title = 'Reto 3 en 7';
  }, []);
  // Animation variants for staggered entrance
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <div className="min-h-screen bg-brand-navy text-white font-poppins selection:bg-brand-gold selection:text-brand-navy flex flex-col" data-id="element-0">
      {/* Subtle Venezuelan Flag Top Border */}
      <div className="h-1.5 w-full flex" data-id="element-1">
        <div className="h-full w-1/2 bg-brand-vzlaYellow" data-id="element-2"></div>
        <div className="h-full w-1/4 bg-brand-vzlaBlue" data-id="element-3"></div>
        <div className="h-full w-1/4 bg-brand-vzlaRed" data-id="element-4"></div>
      </div>

      <main className="flex-grow flex items-center justify-center px-4 py-12 sm:py-20" data-id="element-5">
        <motion.div className="max-w-3xl w-full mx-auto space-y-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-6">
          {/* Header Section */}
          <motion.div variants={itemVariants} className="text-center space-y-4" data-id="element-7">
            <h2 className="text-gray-400 font-medium tracking-widest uppercase text-sm sm:text-base mb-2" data-id="element-8">
              Atención Emprendedores
            </h2>
            <h1 className="font-montserrat text-4xl sm:text-5xl md:text-6xl font-black leading-tight text-white" data-id="element-9">
              Olvídate del Bloqueo de{' '}
              <span className="text-red-500 relative inline-block" data-id="element-10">
                Hotmart
                <span className="absolute top-1/2 left-0 w-full h-1.5 bg-red-600 -rotate-6 transform -translate-y-1/2 opacity-80" data-id="element-11"></span>
              </span>
            </h1>
            <p className="font-montserrat text-2xl sm:text-3xl md:text-4xl font-bold text-brand-gold mt-4" data-id="element-12">
              Emprende un Negocio Digital desde Venezuela
            </p>

            {/* Hero Image */}
            <motion.div variants={itemVariants} className="pt-6" data-id="element-13">
              <img src="https://cdn.magicpatterns.com/uploads/puuB8L6wcPUtmz4L6sdgUm/komvii.png" alt="Komvii - Plataforma de productos digitales" className="mx-auto w-full max-w-xs sm:max-w-sm md:max-w-md drop-shadow-2xl" data-id="element-14" />
            </motion.div>

            {/* USDT Commissions Text */}
            <motion.p variants={itemVariants} className="font-montserrat text-lg sm:text-xl md:text-2xl font-bold text-white leading-snug pt-2" data-id="element-15">
              Aprende a vender productos digitales de otras personas y ganar
              comisiones en <span className="text-brand-gold" data-id="element-16">USDT</span>,
              facturando de <span className="text-brand-gold" data-id="element-17">1k a 5k</span>{' '}
              desde Venezuela
            </motion.p>

            {/* Date & Time */}
            <motion.div variants={itemVariants} className="inline-flex items-center justify-center gap-2 bg-brand-card border border-brand-gold/30 rounded-full px-6 py-3 mt-4" data-id="element-18">
              <ClockIcon className="w-5 h-5 text-brand-gold" data-id="element-19" />
              <span className="font-montserrat font-bold text-brand-gold text-lg sm:text-xl" data-id="element-20">
                Próximo Jueves 8:00 PM
              </span>
            </motion.div>

            {/* Hero CTA Button */}
            <motion.a href="https://chat.whatsapp.com/CjIirUBD1vV3Z5ZqJjDWha" target="_blank" rel="noopener noreferrer" variants={itemVariants} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="inline-flex items-center justify-center gap-3 bg-brand-whatsapp hover:bg-brand-whatsappHover text-white font-bold text-lg sm:text-xl py-5 px-8 sm:px-12 rounded-full shadow-[0_0_30px_rgba(37,211,102,0.3)] hover:shadow-[0_0_40px_rgba(37,211,102,0.5)] transition-all duration-300 w-full sm:w-auto animate-pulse-slow mt-6" data-id="element-21">
              <WhatsAppIcon className="w-7 h-7" data-id="element-22" />
              <span data-id="element-23">Quiero unirme al Grupo de Whatsapp</span>
            </motion.a>
          </motion.div>

          {/* Value Proposition Card */}
          <motion.div variants={itemVariants} className="bg-brand-card border border-gray-800 rounded-2xl p-6 sm:p-10 shadow-2xl relative overflow-hidden" data-id="element-24">
            {/* Decorative background glow */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-brand-gold opacity-10 rounded-full blur-3xl" data-id="element-25"></div>
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-brand-vzlaBlue opacity-10 rounded-full blur-3xl" data-id="element-26"></div>

            <div className="relative z-10 space-y-6 text-center sm:text-left" data-id="element-27">
              <p className="text-lg sm:text-xl font-medium text-gray-200 text-center" data-id="element-28">
                Participa en nuestra Clase Online Gratuita donde descubrirás: 👇
              </p>

              <div className="bg-brand-navy/50 rounded-xl p-6 border border-gray-800/50" data-id="element-29">
                <ul className="space-y-4 text-base sm:text-lg leading-relaxed text-gray-300" data-id="element-30">
                  <li className="flex items-start gap-3" data-id="element-31">
                    <span className="text-brand-gold font-bold text-lg mt-0.5" data-id="element-32">
                      1.
                    </span>
                    <span data-id="element-33">
                      La manera más simple de emprender digitalmente desde
                      Venezuela utilizando{' '}
                      <strong className="text-white font-semibold" data-id="element-34">
                        Komvii
                      </strong>
                      .
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-35">
                    <span className="text-brand-gold font-bold text-lg mt-0.5" data-id="element-36">
                      2.
                    </span>
                    <span data-id="element-37">
                      Cómo vender productos digitales sin bloqueos{' '}
                      <strong className="text-white font-semibold" data-id="element-38">
                        (Adiós VPN)
                      </strong>
                      .
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-39">
                    <span className="text-brand-gold font-bold text-lg mt-0.5" data-id="element-40">
                      3.
                    </span>
                    <span data-id="element-41">
                      La plataforma venezolana que te paga en{' '}
                      <strong className="text-white font-semibold" data-id="element-42">
                        USDT o Bs
                      </strong>
                      .
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-43">
                    <span className="text-brand-gold font-bold text-lg mt-0.5" data-id="element-44">
                      4.
                    </span>
                    <span data-id="element-45">
                      El paso a paso para iniciar a vender{' '}
                      <strong className="text-white font-semibold" data-id="element-46">
                        incluso sin experiencia
                      </strong>
                      .
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Class Info Section */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center gap-6 bg-gradient-to-r from-red-950/30 to-brand-navy rounded-2xl p-6 border border-red-900/20" data-id="element-47">
            <div className="flex-shrink-0 bg-red-600/20 p-4 rounded-full" data-id="element-48">
              <YouTubeIcon className="w-10 h-10 text-red-500" data-id="element-49" />
            </div>
            <p className="text-center sm:text-left text-gray-300 text-sm sm:text-base leading-relaxed" data-id="element-50">
              La clase se transmitirá a través de Youtube, y para asistir debes
              Unirte al Grupo Oficial de Whatsapp donde se enviará el link de
              acceso, así como toda la información y recursos necesarios.
            </p>
          </motion.div>

          {/* CTA Section */}
          <motion.div variants={itemVariants} className="text-center space-y-8 pt-4" data-id="element-51">
            <p className="text-lg font-medium text-gray-200" data-id="element-52">
              Haz clic abajo en el botón para unirte al Grupo de Whatsapp
            </p>

            <motion.a href="https://chat.whatsapp.com/CjIirUBD1vV3Z5ZqJjDWha" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} className="inline-flex items-center justify-center gap-3 bg-brand-whatsapp hover:bg-brand-whatsappHover text-white font-bold text-lg sm:text-xl py-5 px-8 sm:px-12 rounded-full shadow-[0_0_30px_rgba(37,211,102,0.3)] hover:shadow-[0_0_40px_rgba(37,211,102,0.5)] transition-all duration-300 w-full sm:w-auto animate-pulse-slow" data-id="element-53">
              <WhatsAppIcon className="w-7 h-7" data-id="element-54" />
              <span data-id="element-55">Quiero unirme al Grupo de Whatsapp</span>
            </motion.a>

            <p className="font-montserrat font-bold text-2xl text-brand-gold pt-4" data-id="element-56">
              ¡Te veo dentro! 👍
            </p>
          </motion.div>
        </motion.div>
      </main>
    </div>;
}